// src/services/SettingsService.js
import auditService from './AuditService';
import storage from './StorageAdapter';

// Canonical default branding colors — import this constant instead of hardcoding in other files.
export const REPORT_COLOR_DEFAULTS = {
  primary: '#19376e',
  secondary: '#3c64b4',
  accent: '#5082d2'
};

class SettingsService {
  constructor() {
    this.STORAGE_KEY = 'rms_settings';
    this.listeners = [];
    this.DEFAULTS = {
      session: {
        sessionTimeout: 8 * 60 * 60 * 1000,     // 8 timmar
        inactivityTimeout: 30 * 60 * 1000,      // 30 minuter
        warningTime: 5 * 60 * 1000,             // 5 minuter varning
        inactivityWarningTime: 2 * 60 * 1000,   // 2 minuter inaktivitetsvarning
        activityCheckInterval: 60 * 1000        // 1 minut kontrollintervall
      },
      audit: {
        retentionDays: 30,
        maxLogs: 1000
      },
      ui: {
        theme: 'default',
        language: 'en',
        displayNameFormat: 'firstLast'  // 'firstLast' | 'lastFirst' | 'email'
      },
      modules: {
        items: [
          { id: 'dashboard', route: '/dashboard', enabled: true, alwaysEnabled: true, order: 0 },
          { id: 'bwra', route: '/bwra', enabled: true, alwaysEnabled: false, order: 1 },
          { id: 'geographical-risk', route: '/geographical-risk', enabled: true, alwaysEnabled: false, order: 2 },
          { id: 'customer-risk', route: '/customer-risk', enabled: true, alwaysEnabled: false, order: 3 },
          { id: 'transaction-monitoring', route: '/transaction-monitoring', enabled: true, alwaysEnabled: false, order: 4 },
          { id: 'periodic-reporting', route: '/periodic-reporting', enabled: true, alwaysEnabled: false, order: 5 },
          { id: 'admin', route: '/admin', enabled: true, alwaysEnabled: true, order: 6 }
        ]
      },
      branding: {
        companyName: '',
        companySubtitle: '',
        orgNumber: '',
        logoBase64: '',
        reportColorPrimary: REPORT_COLOR_DEFAULTS.primary,
        reportColorSecondary: REPORT_COLOR_DEFAULTS.secondary,
        reportColorAccent: REPORT_COLOR_DEFAULTS.accent
      },
      transactionMonitoring: {
        validationHistoryLimit: 100
      }
    };

    // Admin limits (future-proofing för user restrictions)
    this.ADMIN_LIMITS = {
      session: {
        maxSessionTimeout: 24 * 60 * 60 * 1000,     // Max 24 timmar
        minSessionTimeout: 1 * 60 * 60 * 1000,      // Min 1 timme
        maxInactivityTimeout: 2 * 60 * 60 * 1000,   // Max 2 timmar
        minInactivityTimeout: 5 * 60 * 1000,        // Min 5 minuter
        maxWarningTime: 15 * 60 * 1000,             // Max 15 minuter
        minWarningTime: 1 * 60 * 1000               // Min 1 minut
      },
      audit: {
        maxRetentionDays: 90,
        minRetentionDays: 7,
        maxLogs: 5000,
        minLogs: 100
      },
      transactionMonitoring: {
        maxValidationHistoryLimit: 10000,
        minValidationHistoryLimit: 10
      }
    };

    // User permissions (future-proofing)
    this.USER_PERMISSIONS = {
      admin: {
        canChangeSession: true,
        canChangeAudit: true,
        canChangeUI: true,
        canChangeUserSettings: true,
        canChangeBranding: true,
        canChangeModules: true,
        canChangeTransactionMonitoring: true
      },
      user: {
        canChangeSession: true,    // Inom admin-gränser
        canChangeAudit: false,
        canChangeUI: true,
        canChangeUserSettings: false,
        canChangeBranding: false,
        canChangeModules: false,
        canChangeTransactionMonitoring: false
      },
      viewer: {
        canChangeSession: false,
        canChangeAudit: false,
        canChangeUI: true,
        canChangeUserSettings: false,
        canChangeBranding: false,
        canChangeModules: false,
        canChangeTransactionMonitoring: false
      }
    };
  }

  // Hämta alla inställningar
  getSettings() {
    const settings = storage.getJSON(this.STORAGE_KEY, {});
    return this.deepMerge(this.DEFAULTS, settings);
  }

  // Hämta specifik kategori
  getCategory(category) {
    const settings = this.getSettings();
    return settings[category] || this.DEFAULTS[category];
  }

  // Hämta specifik inställning
  getSetting(category, key) {
    const categorySettings = this.getCategory(category);
    return categorySettings[key];
  }

  // Spara inställningar
  async saveSettings(newSettings, userRole = 'admin') {
    try {
      const currentSettings = this.getSettings();
      const permissions = this.USER_PERMISSIONS[userRole];
      
      // Validera behörigheter
      const validatedSettings = this.validatePermissions(newSettings, permissions);
      
      // Validera värden mot admin-gränser
      const limitedSettings = this.validateLimits(validatedSettings);
      
      // Merge med befintliga inställningar
      const finalSettings = this.deepMerge(currentSettings, limitedSettings);
      
      // Spara
      await storage.setJSON(this.STORAGE_KEY, finalSettings);

      // Logga ändring
      auditService.logUpdate('Admin', 'Settings', 'system', {
        changes: limitedSettings,
        userRole
      }, 'System settings updated');

      this.notifyListeners();

      return { success: true, settings: finalSettings };
      
    } catch (error) {
      console.error('[SETTINGS] Error saving settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Uppdatera specifik inställning
  async updateSetting(category, key, value, userRole = 'admin') {
    const newSettings = {
      [category]: {
        [key]: value
      }
    };
    
    return this.saveSettings(newSettings, userRole);
  }

  // Återställ till default
  async resetToDefaults(category = null) {
    try {
      let settings;

      if (category) {
        const currentSettings = this.getSettings();
        settings = {
          ...currentSettings,
          [category]: this.DEFAULTS[category]
        };
      } else {
        settings = this.DEFAULTS;
      }

      await storage.setJSON(this.STORAGE_KEY, settings);

      auditService.logUpdate('Admin', 'Settings', 'system', {
        action: 'RESET_TO_DEFAULTS',
        category: category || 'ALL'
      }, `Settings reset to defaults: ${category || 'ALL'}`);

      this.notifyListeners();

      return { success: true, settings };

    } catch (error) {
      console.error('[SETTINGS] Error resetting settings:', error);
      return { success: false, error: error.message };
    }
  }

  // Validera behörigheter
  validatePermissions(settings, permissions) {
    const validatedSettings = {};
    const permissionMap = {
      session: 'canChangeSession',
      audit: 'canChangeAudit',
      ui: 'canChangeUI'
    };

    Object.keys(settings).forEach(category => {
      const permKey = permissionMap[category] || `canChange${category.charAt(0).toUpperCase() + category.slice(1)}`;
      const canChange = permissions[permKey];

      if (canChange) {
        validatedSettings[category] = settings[category];
      }
    });

    return validatedSettings;
  }

  // Validera värden mot admin-gränser
  validateLimits(settings) {
    const validatedSettings = { ...settings };
    
    // Validera session settings
    if (validatedSettings.session) {
      const sessionLimits = this.ADMIN_LIMITS.session;
      const session = validatedSettings.session;
      
      if (session.sessionTimeout) {
        session.sessionTimeout = Math.max(
          sessionLimits.minSessionTimeout,
          Math.min(sessionLimits.maxSessionTimeout, session.sessionTimeout)
        );
      }
      
      if (session.inactivityTimeout) {
        session.inactivityTimeout = Math.max(
          sessionLimits.minInactivityTimeout,
          Math.min(sessionLimits.maxInactivityTimeout, session.inactivityTimeout)
        );
      }
      
      if (session.warningTime) {
        session.warningTime = Math.max(
          sessionLimits.minWarningTime,
          Math.min(sessionLimits.maxWarningTime, session.warningTime)
        );
      }
    }
    
    // Validera audit settings
    if (validatedSettings.audit) {
      const auditLimits = this.ADMIN_LIMITS.audit;
      const audit = validatedSettings.audit;
      
      if (audit.retentionDays) {
        audit.retentionDays = Math.max(
          auditLimits.minRetentionDays,
          Math.min(auditLimits.maxRetentionDays, audit.retentionDays)
        );
      }
      
      if (audit.maxLogs) {
        audit.maxLogs = Math.max(
          auditLimits.minLogs,
          Math.min(auditLimits.maxLogs, audit.maxLogs)
        );
      }
    }

    // Validera transactionMonitoring settings
    if (validatedSettings.transactionMonitoring) {
      const txLimits = this.ADMIN_LIMITS.transactionMonitoring;
      const tx = validatedSettings.transactionMonitoring;

      if (tx.validationHistoryLimit != null) {
        tx.validationHistoryLimit = Math.max(
          txLimits.minValidationHistoryLimit,
          Math.min(txLimits.maxValidationHistoryLimit, tx.validationHistoryLimit)
        );
      }
    }

    return validatedSettings;
  }

  // Hämta admin-gränser för UI
  getAdminLimits() {
    return this.ADMIN_LIMITS;
  }

  // Hämta user permissions
  getUserPermissions(userRole = 'admin') {
    return this.USER_PERMISSIONS[userRole];
  }

  // Formatera tid för UI
  formatTimeForUI(milliseconds) {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }

  // Konvertera från UI-format till millisekunder
  parseTimeFromUI(timeString) {
    const parts = timeString.toLowerCase().split(' ');
    let totalMs = 0;
    
    parts.forEach(part => {
      if (part.includes('h')) {
        const hours = parseInt(part.replace('h', ''));
        totalMs += hours * 60 * 60 * 1000;
      } else if (part.includes('m')) {
        const minutes = parseInt(part.replace('m', ''));
        totalMs += minutes * 60 * 1000;
      }
    });
    
    return totalMs;
  }

  // Deep merge utility
  deepMerge(target, source) {
    const result = { ...target };
    
    Object.keys(source).forEach(key => {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(target[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    });
    
    return result;
  }

  // Hämta modulkonfiguration (sorterad, med defaults som fallback)
  getModuleConfig() {
    const settings = this.getSettings();
    let items = settings.modules?.items || this.DEFAULTS.modules.items;
    // Migrate ARB → BWRA in stored module config
    const arbItem = items.find(m => m.id === 'arb');
    if (arbItem && !items.find(m => m.id === 'bwra')) {
      items = items.map(m => m.id === 'arb' ? { ...m, id: 'bwra', route: '/bwra' } : m);
      // Write directly to storage to avoid calling notifyListeners() during getModuleConfig(),
      // which would cause an infinite loop if a listener triggers getModuleConfig().
      const stored = storage.getJSON(this.STORAGE_KEY, {});
      if (stored.modules?.items) {
        stored.modules.items = stored.modules.items.map(m =>
          m.id === 'arb' ? { ...m, id: 'bwra', route: '/bwra' } : m
        );
        storage.setJSON(this.STORAGE_KEY, stored);
      }
    }
    // Migrate old sidebar order: if stored config has customer-risk at order 1 and bwra at order 3,
    // it means the user has the pre-INFRA-021 default. Reset to the new BWRA-first ordering.
    const storedCustomerRisk = items.find(m => m.id === 'customer-risk');
    const storedBwra = items.find(m => m.id === 'bwra');
    if (storedCustomerRisk?.order === 1 && storedBwra?.order === 3) {
      const newOrderById = { 'dashboard': 0, 'bwra': 1, 'geographical-risk': 2, 'customer-risk': 3,
        'transaction-monitoring': 4, 'periodic-reporting': 5, 'admin': 6 };
      items = items.map(m => newOrderById[m.id] !== undefined ? { ...m, order: newOrderById[m.id] } : m);
      const stored = storage.getJSON(this.STORAGE_KEY, {});
      if (stored.modules?.items) {
        stored.modules.items = stored.modules.items.map(m =>
          newOrderById[m.id] !== undefined ? { ...m, order: newOrderById[m.id] } : m
        );
        storage.setJSON(this.STORAGE_KEY, stored);
      }
    }
    // Merge with defaults to ensure all modules exist
    const defaults = this.DEFAULTS.modules.items;
    const merged = defaults.map(def => {
      const saved = items.find(item => item.id === def.id);
      const result = saved ? { ...def, ...saved } : def;
      // Enforce alwaysEnabled from defaults (saved data can never override)
      result.alwaysEnabled = def.alwaysEnabled;
      if (result.alwaysEnabled) result.enabled = true;
      return result;
    });
    return merged.sort((a, b) => a.order - b.order);
  }

  // Hämta bara aktiverade moduler (sorterade)
  getEnabledModules() {
    return this.getModuleConfig().filter(m => m.enabled);
  }

  // Spara modulkonfiguration
  async saveModuleConfig(items) {
    return this.saveSettings({ modules: { items } });
  }

  // Export settings för backup
  exportSettings() {
    const settings = this.getSettings();
    const exportData = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      settings
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rms-settings-${new Date().toISOString().split('T')[0]}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    auditService.logExport('Admin', 'settings-export', 1);
  }

  subscribe(listener) {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  notifyListeners() {
    this.listeners.forEach(listener => listener());
  }

  // Import settings från backup
  async importSettings(fileContent) {
    try {
      const importData = JSON.parse(fileContent);
      
      if (importData.settings) {
        const result = this.saveSettings(importData.settings);
        
        if (result.success) {
          auditService.logDataImport('Admin', 'settings-import', 1, true);
        }
        
        return result;
      } else {
        throw new Error('Invalid settings file format');
      }
    } catch (error) {
      console.error('[SETTINGS] Error importing settings:', error);
      auditService.logDataImport('Admin', 'settings-import', 1, false);
      return { success: false, error: error.message };
    }
  }
}

// Singleton instance
const settingsService = new SettingsService();

export default settingsService;
